import { errorReport, serializeErrorReport } from '../shared/error-report.js';
import { queueAnalyticsEvent } from './analytics-delivery.js';
import { isPlausibleEventInteractive } from '../shared/plausible-events.js';
import { APP_VERSION } from './version.js';
import { analyticsLanguageContext, analyticsPageContext } from './analytics-context.js';
import {
  getOrCreateAnalyticsVisitorId,
  isAnalyticsOptedOut,
} from './analytics-identity.js';
import {
  EVENT_PROP_ALLOWLIST,
  type AnalyticsEventName,
  type AnalyticsEventProps,
  type ExactAnalyticsProps,
} from '../shared/analytics-schema.js';

interface PageAnalyticsProperties {
  [name: string]: string;
}

export function errorBlob(cause: unknown, fallback: string): string {
  return serializeErrorReport({ ...errorReport(cause, fallback), kind: fallback, app_version: APP_VERSION });
}

export function trackPageEvent<
  Event extends AnalyticsEventName<typeof EVENT_PROP_ALLOWLIST>,
  Props extends AnalyticsEventProps<typeof EVENT_PROP_ALLOWLIST, Event> =
    AnalyticsEventProps<typeof EVENT_PROP_ALLOWLIST, Event>,
>(
  app: string,
  event: Event,
  props?: ExactAnalyticsProps<typeof EVENT_PROP_ALLOWLIST, Event, Props>,
): void;
export function trackPageEvent(
  app: string,
  event: string,
  props: Record<string, string> = {},
): void {
  if (isAnalyticsOptedOut()) return;
  const context = analyticsPageContext();
  const visitorId = getOrCreateAnalyticsVisitorId();
  const cleanProps: PageAnalyticsProperties = {
    session_id: context.session_id,
    app,
    device_class: context.device_class,
    os_family: context.os_family,
    app_version: APP_VERSION,
    ...analyticsLanguageContext(),
    ...props,
  };
  if (visitorId) cleanProps.visitor_id = visitorId;
  try {
    const { visitor_id: _visitorId, session_id: _sessionId, ...plausibleProps } = cleanProps;
    window.plausible?.(event, { props: plausibleProps, interactive: isPlausibleEventInteractive(event, plausibleProps) });
  } catch {
    // Third-party analytics must never interrupt the page flow.
  }

  queueAnalyticsEvent('/api/analytics', { event, props: cleanProps });
}
